// Organisation helpers for the projects-list org filter.
//
// A project's counterparties live in free-text fields - landlord,
// tenant, primary EPC. The org filter treats all three as a
// single flat list of organisations ("as though they are all
// equivalent"), but a selected org matches a project whenever it
// appears in ANY of those roles, so an org that is a landlord on one
// project and a tenant on another matches both.

// Any record that names its counterparty orgs across the three roles. Both
// ``Project`` (projects page) and ``ProjectSummary`` (operations page) satisfy
// this, so both surfaces share one org-filter implementation.
export interface OrgBearing {
  landlord?: string | null;
  tenant?: string | null;
  primary_epc?: string | null;
}

// The fields that name an organisation. Order here is the order used for
// de-duplication when the same name appears in two roles.
const ORG_FIELDS: Array<keyof OrgBearing> = [
  "landlord",
  "tenant",
  "primary_epc",
];

// Distinct, trimmed, non-empty org names associated with one project,
// across every role. Trimmed so " Acme " and "Acme" don't read as two
// orgs; de-duped case-insensitively, keeping the first-seen casing.
export const orgsForProject = (project: OrgBearing): string[] => {
  const seen = new Map<string, string>();
  for (const field of ORG_FIELDS) {
    const raw = project[field];
    if (typeof raw !== "string") continue;
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()];
};

// The full alphabetical list of distinct org names across all projects,
// for the filter dropdown. Case-insensitive de-dupe + sort.
export const allOrgs = (projects: OrgBearing[]): string[] => {
  const seen = new Map<string, string>();
  for (const project of projects) {
    for (const name of orgsForProject(project)) {
      const key = name.toLowerCase();
      if (!seen.has(key)) seen.set(key, name);
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
  );
};

// True when the project is associated with at least one of the selected
// orgs (OR semantics), in any role. ``selectedKeys`` is a set of
// lower-cased org names so the membership test is case-insensitive.
export const projectMatchesOrgs = (
  project: OrgBearing,
  selectedKeys: Set<string>,
): boolean => {
  if (selectedKeys.size === 0) return true;
  return orgsForProject(project).some((name) =>
    selectedKeys.has(name.toLowerCase()),
  );
};

// True when the free-text query is an empty/blank string, or a
// case-insensitive substring of the project's name + address. Shared by the
// projects, operations and dashboard filter bars so their search behaviours
// can't drift (callers pass their own derived display name).
export const projectMatchesSearch = (
  name: string,
  address: string | null | undefined,
  query: string,
): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${name} ${address ?? ""}`.toLowerCase().includes(q);
};
